import React, { useState, useEffect } from 'react'

// Constants
import { PLAN_TO_DURATION_LABEL } from 'modules/workspace/constants'

// Utils
import { formatPriceShort } from 'utils/money'
import dateFormat from 'utils/localizedDateFormat'
import { getDiscountError, getAppliedDiscount } from 'modules/workspace/utils'

// Components
import Spinner from 'components/Spinner'
import Button from 'components/Button'
import Breakpoint from 'components/Breakpoint'
import SeatSummary from './components/SeatsSummary'
import Discount from '../../components/Discount'
import { CollapsibleSection } from '../../components'

// Styles
import {
  Section,
  Title,
  Wrapper,
  PlanPrice,
  HalfDivider,
  DrawerButton,
  Chevron,
  Paragraph,
} from './WorkspaceSubscribeSummary.styles'

// Types
import {
  useDraftBillingSummaryQuery,
  useDraftBillingSummaryLazyQuery,
  BillingPlanFragment,
} from 'gqlTypes'

interface WorkspaceSubscribeSummaryProps {
  plan: BillingPlanFragment
  trialEnd: string
  numberEditors: number
  numberViewers: number
  numberExtraSeats: number
  className?: string
  storedDiscountCode?: string
  onValidDiscountCode: (discountCode: string) => void
  onClearDiscountCode: () => void
}

// Helpers
const formatPrice = (price: number) =>
  formatPriceShort(price, { trimDecimalIfZero: false })

const buildDate = (date: string) => dateFormat(new Date(date))

export const WorkspaceSubscribeSummary: React.FC<WorkspaceSubscribeSummaryProps> = props => {
  const {
    plan,
    trialEnd,
    numberEditors,
    numberExtraSeats,
    numberViewers,
    className,
    onValidDiscountCode,
    onClearDiscountCode,
    storedDiscountCode,
  } = props

  /* Mobile collapsed drawer controller */
  const [mobileCollapsed, setMobileCollapsed] = useState(true)

  /*
    Get Billing Summary
    If we stored a valid discount code in the state, this summary
    will return a CouponInfo with the valid coupon
  */
  const { loading, data, error, refetch } = useDraftBillingSummaryQuery({
    variables: {
      planId: plan.id,
      seats: numberEditors + numberExtraSeats,
      // Get the summary with the stored valid discount code
      promotionCode: storedDiscountCode,
    },
  })

  // The actual valid discount code
  const appliedDiscount = getAppliedDiscount(
    data?.draftBillingSummary?.couponInfo || undefined
  )

  // We first use this lazy query to preview if the discount is valid
  const [
    getSummaryPreviewWithDiscount,
    summaryPreviewWithDiscount,
  ] = useDraftBillingSummaryLazyQuery({
    fetchPolicy: 'network-only',
  })

  // The preview of the discount code
  const previewCouponInfo =
    summaryPreviewWithDiscount.data?.draftBillingSummary?.couponInfo ||
    undefined
  const discountError = getDiscountError(previewCouponInfo)

  // If there is no discount error, save the discount code to the state
  // this is the discount code that will be applied when subscribing
  useEffect(() => {
    const hasDiscountError = !!getDiscountError(previewCouponInfo)

    // Clear stored discount code if there's an error and a previously stored discount
    if (hasDiscountError && !!storedDiscountCode) {
      onClearDiscountCode()
    }

    // Don't store the discount code if there's an error
    if (hasDiscountError) {
      return
    }

    // The coupon code is valid and is stored in the state.
    // It is only actually applied when subscribing the workspace. Check "useSubscribeToPlan"
    previewCouponInfo && onValidDiscountCode(previewCouponInfo.promotionCode)
  }, [
    previewCouponInfo,
    onClearDiscountCode,
    onValidDiscountCode,
    storedDiscountCode,
  ])

  const handleApplyDiscount = (discountCode: string) =>
    getSummaryPreviewWithDiscount({
      variables: {
        planId: plan.id,
        seats: numberEditors + numberExtraSeats,
        promotionCode: discountCode,
      },
    })

  const renderDiscountUI = (
    <Discount
      onApplyDiscountCode={handleApplyDiscount}
      appliedDiscountCodes={storedDiscountCode ? [storedDiscountCode] : []}
      error={discountError}
      isDisabled={loading || summaryPreviewWithDiscount.loading}
      isLoading={summaryPreviewWithDiscount.loading}
    />
  )

  let children = null

  if (data && !error) {
    const { draftBillingSummary } = data
    const isOnTrial = new Date(trialEnd) > new Date()

    children = (
      <>
        <CollapsibleSection mobileOnly collapsed={mobileCollapsed}>
          <Section>
            <SeatSummary
              numberEditors={numberEditors}
              numberExtraSeats={numberExtraSeats}
              numberViewers={numberViewers}
              pricePerSeat={draftBillingSummary.memberBillingCycleAmount}
            />
          </Section>
          <>
            <Breakpoint on="md">
              <Section>{renderDiscountUI}</Section>
            </Breakpoint>
            {appliedDiscount && (
              <Section>
                <HalfDivider>
                  <Paragraph>
                    <em>Discount ({appliedDiscount.code.toUpperCase()})</em>
                  </Paragraph>
                  <Paragraph>{formatPrice(-appliedDiscount.price)}</Paragraph>
                </HalfDivider>
              </Section>
            )}
          </>
          <CollapsibleSection collapsed={!isOnTrial}>
            <Section>
              <Paragraph>
                Your <strong>trial will end on {buildDate(trialEnd)}</strong>.
                At this point, we will charge you for the total number of Editor
                seats in your workspace.
              </Paragraph>
            </Section>
          </CollapsibleSection>
        </CollapsibleSection>

        <Section>
          <HalfDivider>
            <Title as="p">
              {isOnTrial ? 'Total due after trial' : 'Total due'}
            </Title>
            <Title as="p">
              {loading && (
                <>
                  <Spinner size="1em" primary />
                  &nbsp;&nbsp;
                </>
              )}
              {formatPrice(draftBillingSummary.billingCycleAmount)} /{' '}
              {PLAN_TO_DURATION_LABEL[plan.type]}
            </Title>
          </HalfDivider>
        </Section>

        <CollapsibleSection mobileOnly collapsed={mobileCollapsed}>
          <Section>
            <Paragraph>Excludes taxes</Paragraph>
          </Section>
        </CollapsibleSection>
      </>
    )
  } else if (loading) {
    children = (
      <Section>
        <HalfDivider>
          <Paragraph>Loading your bill summary…</Paragraph>
          <Spinner primary />
        </HalfDivider>
      </Section>
    )
  } else {
    children = (
      <Section>
        <HalfDivider>
          <Paragraph>Sorry — we could not update your summary.</Paragraph>
          <Button variant="primary" loading={loading} onClick={() => refetch()}>
            Refresh
          </Button>
        </HalfDivider>
      </Section>
    )
  }

  return (
    <Wrapper className={className}>
      <Section>
        <Title>Your Bill Summary</Title>
        <PlanPrice>
          <strong>{formatPrice(plan.invoicePrice)}</strong> per Seat, per{' '}
          {PLAN_TO_DURATION_LABEL[plan.type].toLowerCase()}
        </PlanPrice>
        <DrawerButton
          $isOpen={mobileCollapsed}
          onClick={() => setMobileCollapsed(state => !state)}>
          <span className="sr-only">
            {mobileCollapsed ? 'Open summary drawer' : 'Close summary drawer'}
          </span>
          <Chevron />
        </DrawerButton>
      </Section>
      <Breakpoint on="md">
        {matches => !matches && <Section>{renderDiscountUI}</Section>}
      </Breakpoint>
      {children}
    </Wrapper>
  )
}
